/**
 * Organizations — Admin page listing all schools/camps/organizations.
 * Route: /admin/organizations
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/useToast';
import { db } from '@/lib/firebase';
import {
  collection,
  addDoc,
  getDocs,
  query,
  orderBy,
  Timestamp,
} from 'firebase/firestore';
import { Plus, Building2, ExternalLink, ChevronDown, ChevronUp } from 'lucide-react';

// ─── Types ────────────────────────────────────────────────────────────────────

interface Org {
  id: string;
  name: string;
  type: 'school' | 'camp';
  slug: string;
  season: string;
  description: string;
  contactEmail: string;
  accessCode: string;
  createdAt: Date;
  updatedAt: Date;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function generateSlug(name: string): string {
  return name
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '');
}

function generateAccessCode(): string {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let code = '';
  for (let i = 0; i < 6; i++) {
    code += chars[Math.floor(Math.random() * chars.length)];
  }
  return code;
}

// ─── New Org Form ──────────────────────────────────────────────────────────────

interface NewOrgFormState {
  name: string;
  type: 'school' | 'camp';
  season: string;
  description: string;
  contactEmail: string;
}

const BLANK_FORM: NewOrgFormState = {
  name: '',
  type: 'school',
  season: '',
  description: '',
  contactEmail: '',
};

// ─── Main Component ───────────────────────────────────────────────────────────

export default function Organizations() {
  const navigate = useNavigate();
  const { role, loading: authLoading } = useAuth();
  const { toast } = useToast();

  const [orgs, setOrgs] = useState<Org[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<NewOrgFormState>(BLANK_FORM);
  const [accessCode] = useState<string>(() => generateAccessCode());
  const [saving, setSaving] = useState(false);

  // Redirect non-admins
  useEffect(() => {
    if (!authLoading && role !== 'admin') {
      navigate('/dashboard');
    }
  }, [authLoading, role, navigate]);

  const fetchOrgs = async () => {
    try {
      setLoading(true);
      const q = query(collection(db, 'organizations'), orderBy('createdAt', 'desc'));
      const snap = await getDocs(q);
      const docs: Org[] = snap.docs.map(d => {
        const data = d.data();
        return {
          id: d.id,
          name: data.name ?? '',
          type: data.type ?? 'school',
          slug: data.slug ?? '',
          season: data.season ?? '',
          description: data.description ?? '',
          contactEmail: data.contactEmail ?? '',
          accessCode: data.accessCode ?? '',
          createdAt: (data.createdAt as Timestamp)?.toDate() ?? new Date(),
          updatedAt: (data.updatedAt as Timestamp)?.toDate() ?? new Date(),
        };
      });
      setOrgs(docs);
    } catch (err) {
      console.error('Failed to fetch organizations:', err);
      toast({ title: 'Error loading organizations', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!authLoading && role === 'admin') {
      fetchOrgs();
    }
  }, [authLoading, role]);

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast({ title: 'Name is required', type: 'error' });
      return;
    }
    try {
      setSaving(true);
      const slug = generateSlug(form.name);
      await addDoc(collection(db, 'organizations'), {
        name: form.name.trim(),
        type: form.type,
        slug,
        season: form.season.trim(),
        description: form.description.trim(),
        contactEmail: form.contactEmail.trim(),
        accessCode,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      });
      toast({ title: 'Organization created', type: 'success' });
      setShowForm(false);
      setForm(BLANK_FORM);
      await fetchOrgs();
    } catch (err) {
      console.error('Failed to create organization:', err);
      toast({ title: 'Failed to create organization', type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const slug = generateSlug(form.name);

  if (authLoading || (loading && orgs.length === 0)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-shuttlebee-yellow border-t-transparent rounded-full animate-spin" />
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Organizations</h1>
          <p className="text-sm text-gray-500 mt-1">
            Manage schools and camps with shareable report pages.
          </p>
        </div>
        <Button
          variant="shuttlebee"
          onClick={() => setShowForm(v => !v)}
          className="flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          New Organization
          {showForm ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </Button>
      </div>

      {/* Inline creation form */}
      {showForm && (
        <Card className="mb-6 border-2 border-dashed border-gray-300">
          <CardHeader>
            <CardTitle className="text-base">New Organization</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="e.g. Concordia Prep"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>

              {/* Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                <select
                  value={form.type}
                  onChange={e => setForm(f => ({ ...f, type: e.target.value as 'school' | 'camp' }))}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="school">School</option>
                  <option value="camp">Camp</option>
                </select>
              </div>

              {/* Season */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Season</label>
                <input
                  type="text"
                  value={form.season}
                  onChange={e => setForm(f => ({ ...f, season: e.target.value }))}
                  placeholder="e.g. 2026-2027 or Summer 2026"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>

              {/* Contact Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Contact Email</label>
                <input
                  type="email"
                  value={form.contactEmail}
                  onChange={e => setForm(f => ({ ...f, contactEmail: e.target.value }))}
                  placeholder="contact@school.edu"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>

              {/* Description — full width */}
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <input
                  type="text"
                  value={form.description}
                  onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                  placeholder="Brief description shown on the report page"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>

              {/* Slug preview */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Slug (auto)</label>
                <div className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-500 font-mono">
                  {slug || '—'}
                </div>
              </div>

              {/* Access code */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Access Code (auto)</label>
                <div className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-500 font-mono tracking-widest">
                  {accessCode}
                </div>
              </div>
            </div>

            <div className="flex items-center gap-3 mt-5">
              <Button variant="shuttlebee" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving…' : 'Save Organization'}
              </Button>
              <Button variant="ghost" onClick={() => { setShowForm(false); setForm(BLANK_FORM); }}>
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Org list */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="w-8 h-8 border-4 border-shuttlebee-yellow border-t-transparent rounded-full animate-spin" />
        </div>
      ) : orgs.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Building2 className="w-10 h-10 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500 text-sm">No organizations yet. Create one above.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="flex flex-col gap-4">
          {orgs.map(org => (
            <Card key={org.id} className="hover:shadow-md transition-shadow">
              <CardContent className="py-4 flex items-center justify-between gap-4">
                <div className="flex items-start gap-3 min-w-0">
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-gray-900">{org.name}</span>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        org.type === 'school' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'
                      }`}>
                        {org.type}
                      </span>
                      {org.season && (
                        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                          {org.season}
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-gray-500 mt-1 flex items-center gap-3 flex-wrap">
                      <span>slug: <span className="font-mono">{org.slug}</span></span>
                      <span>code: <span className="font-mono font-semibold tracking-widest">{org.accessCode}</span></span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => window.open(`/school/${org.slug}?code=${org.accessCode}`, '_blank')}
                    className="flex items-center gap-1 text-xs"
                  >
                    <ExternalLink className="w-3.5 h-3.5" />
                    View Report
                  </Button>
                  <Button
                    variant="shuttlebee"
                    size="sm"
                    onClick={() => navigate(`/admin/organizations/${org.id}`)}
                  >
                    Manage
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
